type FeaturebaseTheme = "light" | "dark"
type FeaturebaseMetadata = Record<string, string>
export type FeaturebaseWidgetPage = "MainView" | "RoadmapView" | "CreatePost" | "PostsView" | "ChangelogView" | "HelpView"

interface FeaturebaseFeedbackWidgetConfigParams {
  organization: string
  theme?: FeaturebaseTheme
  locale?: string
  email?: string
  userId?: string
  userHash?: string
}

interface FeaturebaseChangelogWidgetConfigParams {
  organization: string
  theme?: FeaturebaseTheme
  locale?: string
  email?: string
  userId?: string
  userHash?: string
}

interface FeaturebasePanelWidgetConfigParams {
  organization: string
  initialPage?: FeaturebaseWidgetPage
  placement?: "left" | "right"
  fullScreen?: boolean
  disableCloseBtn?: boolean
  theme?: FeaturebaseTheme
  locale?: string
  metadata?: FeaturebaseMetadata
  email?: string
  userId?: string
  userHash?: string
}

type FeaturebaseCallback = (error: unknown, data?: unknown) => void

type FeaturebaseCommand = {
  (action: string, config?: unknown, callback?: FeaturebaseCallback): void
  q?: IArguments[]
}

export interface FeaturebaseWindow extends Window {
  Featurebase?: FeaturebaseCommand
}

interface FeaturebaseUnreadCountCallbackPayload {
  action?: string
  unreadCount?: unknown
}

function removeUndefinedValues<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entryValue]) => entryValue !== undefined)) as T
}

export function buildFeaturebaseFeedbackWidgetConfig({
  organization,
  theme = "light",
  locale = "en",
  email,
  userId,
  userHash,
}: FeaturebaseFeedbackWidgetConfigParams) {
  return removeUndefinedValues({
    organization,
    theme,
    locale,
    email,
    userId,
    userHash,
  })
}

export function buildFeaturebaseChangelogWidgetConfig({
  organization,
  theme = "light",
  locale = "en",
  email,
  userId,
  userHash,
}: FeaturebaseChangelogWidgetConfigParams) {
  return removeUndefinedValues({
    organization,
    dropdown: {
      enabled: false,
    },
    popup: {
      enabled: false,
      autoOpenForNewUpdates: false,
    },
    theme,
    locale,
    email,
    userId,
    userHash,
  })
}

export function buildFeaturebasePanelWidgetConfig({
  organization,
  initialPage = "ChangelogView",
  placement = "left",
  fullScreen = false,
  disableCloseBtn = false,
  theme = "light",
  locale = "en",
  metadata,
  email,
  userId,
  userHash,
}: FeaturebasePanelWidgetConfigParams) {
  return removeUndefinedValues({
    organization,
    initialPage,
    placement,
    fullScreen,
    disableCloseBtn,
    theme,
    locale,
    metadata,
    email,
    userId,
    userHash,
  })
}

export function getFeaturebaseUnreadCountFromCallback(data: unknown) {
  const unreadData = data as FeaturebaseUnreadCountCallbackPayload | null

  if (unreadData?.action !== "unreadChangelogsCountChanged") {
    return null
  }

  const unreadCount = Number(unreadData.unreadCount)

  if (!Number.isFinite(unreadCount) || unreadCount < 0) {
    return null
  }

  return unreadCount
}

export function ensureFeaturebaseCommandQueue(win: FeaturebaseWindow): FeaturebaseCommand {
  if (typeof win.Featurebase !== "function") {
    const queuedFeaturebase = function () {
      ;(queuedFeaturebase.q = queuedFeaturebase.q || []).push(arguments)
    } as FeaturebaseCommand

    win.Featurebase = queuedFeaturebase
  }

  return win.Featurebase
}
